import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UploadProgress {
    id: string;
    fileName: string;
    status: 'uploading' | 'success' | 'error';
    percent: number;
    startTime: number;
    testId?: number;
    stepId?: number;
    stepIndex?: number;
    stepName?: string;
    stepNumber?: number;
    stepDescription?: string;
}

/**
 * Service to track multiple concurrent video uploads
 * Maintains a list of active uploads with their progress
 */
@Injectable({
    providedIn: 'root'
})
export class UploadTrackerService {
    private uploadsSubject = new BehaviorSubject<Map<string, UploadProgress>>(new Map());
    public uploads$: Observable<Map<string, UploadProgress>> = this.uploadsSubject.asObservable();

    /**
     * Start tracking a new upload
     */
    startUpload(
        id: string,
        fileName: string,
        testId?: number,
        stepId?: number,
        stepIndex?: number,
        stepName?: string,
        stepNumber?: number,
        stepDescription?: string
    ): void {
        console.log('[VideoUploadTracker] startUpload called:', {
            id,
            fileName,
            testId,
            stepId,
            stepIndex,
            stepName,
            stepNumber,
            stepDescription
        });
        const uploads = this.uploadsSubject.value;
        uploads.set(id, {
            id,
            fileName,
            status: 'uploading',
            percent: 0,
            startTime: Date.now(),
            testId,
            stepId,
            stepIndex,
            stepName,
            stepNumber,
            stepDescription
        });
        this.uploadsSubject.next(new Map(uploads));
        console.log('[VideoUploadTracker] Upload map size:', uploads.size);
    }

    /**
     * Update upload progress
     */
    updateProgress(id: string, percent: number): void {
        const uploads = this.uploadsSubject.value;
        const upload = uploads.get(id);
        if (upload) {
            upload.percent = Math.min(100, Math.max(0, percent));
            this.uploadsSubject.next(new Map(uploads));
        }
    }

    /**
     * Mark upload as successful
     */
    markSuccess(id: string): void {
        const uploads = this.uploadsSubject.value;
        const upload = uploads.get(id);
        if (upload) {
            upload.status = 'success';
            upload.percent = 100;
            this.uploadsSubject.next(new Map(uploads));

            // Auto-remove after 3 seconds
            setTimeout(() => this.removeUpload(id), 3000);
        }
    }

    /**
     * Mark upload as failed
     */
    markError(id: string): void {
        const uploads = this.uploadsSubject.value;
        const upload = uploads.get(id);
        if (upload) {
            upload.status = 'error';
            this.uploadsSubject.next(new Map(uploads));

            // Auto-remove after 5 seconds
            setTimeout(() => this.removeUpload(id), 5000);
        }
    }

    /**
     * Remove upload from tracking
     */
    removeUpload(id: string): void {
        const uploads = this.uploadsSubject.value;
        uploads.delete(id);
        this.uploadsSubject.next(new Map(uploads));
    }

    /**
     * Get current upload by id
     */
    getUpload(id: string): UploadProgress | undefined {
        return this.uploadsSubject.value.get(id);
    }

    /**
     * Get all current uploads as array
     */
    getAllUploads(): UploadProgress[] {
        return Array.from(this.uploadsSubject.value.values());
    }

    /**
     * Clear all uploads
     */
    clearAll(): void {
        this.uploadsSubject.next(new Map());
    }
}
